import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';

import { SelectDateComponent } from '@app/shared/select-date/select-date.component';
import { RentalDecisionPayload, RentalInterruption, interruptionInProgress } from '@app/shared/_models/sales';
import { C_value, User } from '@app/shared/_models/common';
import { C_VALUES_RENTAL_REASONS } from '@app/shared/constants';
import { EventType } from '@app/shared/constants-server';
import { businessDayOf, dayNoonIso, parseTz, pickedDateStr, todayStr } from '@app/shared/time';
import { addDays } from '@app/sales/rental-billing/rental-days';
import { ValuesService } from '@app/common/values/values.service';
import { CommonService } from '@app/common/common.service';
import { LoginService } from '@app/common/login.service';
import { QuotationsService } from '@app/sales/quotations/quotations.service';

export interface OffHireDialogData {
  orderId: number;
  lineId: number;
  productName: string;
  interruptions: RentalInterruption[]; // telles que stockées : jours en instants de Paris à midi, end inclus
  deliveredDay?: string | null; // jour de Paris du BL de livraison, rappelé à côté de « Dès la livraison »
  fseModelId?: number | null; // data.fse_id du dossier : facturé par FSE, un motif qui porte data.fse_notice (c_values 42) affiche son rappel
  readonly?: boolean;
  onDecision?: () => void; // chaque évènement accepté : l'appelant recharge le dossier à la fermeture, même par Échap
}

export type InterruptionFormMode = 'add' | 'replace' | 'cancel';

@Component({
  selector: 'app-off-hire-dialog',
  standalone: true,
  templateUrl: './off-hire-dialog.component.html',
  styleUrls: ['./off-hire-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatCheckboxModule, MatIconModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatTooltipModule, SelectDateComponent],
})
export class OffHireDialogComponent implements OnInit {
  public dialogRef = inject(MatDialogRef<OffHireDialogComponent, boolean>);
  public data: OffHireDialogData = inject(MAT_DIALOG_DATA);
  private valuesService = inject(ValuesService);
  private commonService = inject(CommonService);
  private loginService = inject(LoginService);
  private quotationsService = inject(QuotationsService);
  private cdr = inject(ChangeDetectorRef);

  public interruptions: RentalInterruption[] = [];
  public reasons: C_value[] = []; // motifs actifs d'usage INTERRUPTION : les seuls proposés
  private reasonNames = new Map<number, string>(); // tout le groupe : une entrée ancienne garde le libellé d'un motif désactivé
  private reasonNotices = new Map<number, string>(); // data.fse_notice des motifs d'interruption : rappel d'avis à la caisse (PSDM art. 30 §2)
  public reasonsState: 'loading' | 'ok' | 'missing' | 'error' = 'loading';
  public savedNotice: { id: number | null; text: string } | null = null;
  private userNames = new Map<number, string>();

  public mode: InterruptionFormMode = 'add';
  public targetId: number | null = null;
  public startDate: Date | undefined;
  public endDate: Date | undefined; // dernier jour non facturé, inclus ; vide = interruption ouverte
  public busy = false;
  public errorMsg = '';
  public changed = false;
  public readonly today = todayStr();
  public readonly yesterday = addDays(this.today, -1); // « Reprise aujourd'hui » : dernier jour non facturé = hier
  public readonly reasonsGroup = C_VALUES_RENTAL_REASONS;

  public form = new FormGroup(
    {
      fromDelivery: new FormControl<boolean>(false, { nonNullable: true }),
      start: new FormControl<string | null>(null), // premier jour non facturé, « AAAA-MM-JJ »
      end: new FormControl<string | null>(null), // dernier jour non facturé, inclus ; null = ouverte (avant le 27/10/2026 : resume, lendemain)
      reason_id: new FormControl<number | null>(null),
      comment: new FormControl<string | null>(null),
      cancel_reason: new FormControl<string | null>(null),
    },
    { validators: [(c: AbstractControl) => this.validate(c)] },
  );

  ngOnInit(): void {
    this.interruptions = this.sorted(this.data.interruptions ?? []);
    this.resetForm();
    this.valuesService.getValuesFresh$(C_VALUES_RENTAL_REASONS).subscribe({
      next: (list: C_value[] | null) => {
        const all = Array.isArray(list) ? list : [];
        all.forEach(v => v.id != null && this.reasonNames.set(v.id, v.name ?? `motif ${v.id}`));
        all.forEach(v => {
          const notice = v.data?.usage === 'INTERRUPTION' && typeof v.data?.fse_notice === 'string' ? v.data.fse_notice.trim() : '';
          if (v.id != null && notice) this.reasonNotices.set(v.id, notice);
        });
        this.reasons = all.filter(v => v.is_active === true && v.data?.usage === 'INTERRUPTION');
        this.reasonsState = this.reasons.length ? 'ok' : 'missing';
        this.cdr.markForCheck();
      },
      error: () => {
        this.reasonsState = 'error';
        this.cdr.markForCheck();
      },
    });
    this.commonService.getUsers().subscribe({
      next: (users: User[]) => {
        (users ?? []).forEach(u => u.id != null && this.userNames.set(u.id, [u.firstname, u.lastname].filter(Boolean).join(' ') || `utilisateur ${u.id}`));
        this.cdr.markForCheck();
      },
      error: () => {}, // l'auteur reste affiché par son identifiant
    });
  }

  get canWrite(): boolean {
    return !this.data.readonly && this.data.orderId > 0 && this.data.lineId > 0;
  }

  get isFse(): boolean {
    return (this.data.fseModelId ?? 0) > 0;
  }

  // Texte du motif lui-même (c_values 42), jamais un libellé du code : seul un dossier facturé par FSE doit aviser la caisse.
  fseNotice(reasonId: number | null | undefined): string | null {
    return this.isFse && reasonId != null ? (this.reasonNotices.get(reasonId) ?? null) : null;
  }

  get pendingNotice(): string | null {
    return this.mode === 'cancel' ? null : this.fseNotice(this.form.value.reason_id);
  }

  // Dossier FSE sans aucun motif paramétré : on le signale plutôt que de taire le rappel.
  get noticeMissing(): boolean {
    return this.isFse && this.reasonsState === 'ok' && this.reasonNotices.size === 0;
  }

  // Mêmes contrôles que parse_decision : end ≥ start (bornes incluses), motif pour add/replace, motif d'annulation pour cancel/replace.
  private validate(c: AbstractControl): ValidationErrors | null {
    const v = c.value;
    const errors: ValidationErrors = {};
    if (this.mode !== 'cancel') {
      if (v.reason_id == null) errors['reasonRequired'] = true;
      if (!v.fromDelivery && !v.start) errors['startRequired'] = true;
      if (!v.fromDelivery && v.start && v.end && v.end < v.start) errors['invertedDates'] = true;
    }
    if (this.mode !== 'add' && !(v.cancel_reason ?? '').trim()) errors['cancelReasonRequired'] = true;
    return Object.keys(errors).length ? errors : null;
  }

  private sorted(list: RentalInterruption[]): RentalInterruption[] {
    return [...list].sort((a, b) => a.id - b.id);
  }

  status(i: RentalInterruption): 'cancelled' | 'running' | 'upcoming' | 'over' {
    if (i.cancelled) return 'cancelled';
    if (interruptionInProgress(i, this.today)) return 'running';
    const start = this.startDay(i);
    return start && start > this.today ? 'upcoming' : 'over';
  }

  // Jours de Paris des bornes stockées ; null = absente (dès la livraison, ouverte).
  startDay(i: RentalInterruption): string | null {
    return businessDayOf(i.start);
  }

  endDay(i: RentalInterruption): string | null {
    return businessDayOf(i.end);
  }

  // Colonne Reprise : premier jour de nouveau facturé = lendemain du end (dernier jour non facturé, inclus), affichage seulement.
  billedAgainOn(i: RentalInterruption): string | null {
    const end = this.endDay(i);
    return end ? addDays(end, 1) : null;
  }

  reasonName(id: number | null | undefined): string {
    if (id == null) return '—';
    return this.reasonNames.get(id) ?? (this.reasonsState === 'loading' ? '…' : `motif ${id} absent de c_values ${C_VALUES_RENTAL_REASONS}`);
  }

  userName(id: number | null | undefined): string {
    return id == null ? '—' : (this.userNames.get(id) ?? `utilisateur ${id}`);
  }

  // Jour « AAAA-MM-JJ » affiché JJ/MM/AAAA sans passer par Date : aucun fuseau ne peut le décaler.
  frDay(day: string | null | undefined): string {
    return day && /^\d{4}-\d{2}-\d{2}$/.test(day) ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}` : (day ?? '');
  }

  frInstant(at: string | null | undefined): string {
    return parseTz(at)?.format('DD/MM/YYYY HH:mm') ?? '';
  }

  private localDate(day: string | null | undefined): Date | undefined {
    const m = day ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(day) : null;
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : undefined;
  }

  onStartDateChange(d: Date | null | undefined): void {
    this.startDate = d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
    this.form.patchValue({ start: pickedDateStr(this.startDate) });
  }

  onEndDateChange(d: Date | null | undefined): void {
    this.endDate = d instanceof Date && !isNaN(d.getTime()) ? d : undefined;
    this.form.patchValue({ end: pickedDateStr(this.endDate) });
  }

  clearEndDate(): void {
    this.endDate = undefined;
    this.form.patchValue({ end: null });
  }

  resetForm(): void {
    this.mode = 'add';
    this.targetId = null;
    this.errorMsg = '';
    this.savedNotice = null;
    this.startDate = this.localDate(this.today);
    this.endDate = undefined;
    this.form.reset({ fromDelivery: false, start: this.today, end: null, reason_id: null, comment: null, cancel_reason: null });
  }

  // Modifier = annuler l'entrée et en poser une nouvelle dans le même évènement (op replace) ; `end` pré-rempli (hier) pour « reprise aujourd'hui ».
  editInterruption(i: RentalInterruption, end?: string): void {
    this.mode = 'replace';
    this.targetId = i.id;
    this.errorMsg = '';
    this.savedNotice = null;
    const start = this.startDay(i);
    const e = end ?? this.endDay(i);
    this.startDate = this.localDate(start);
    this.endDate = this.localDate(e);
    this.form.reset({ fromDelivery: i.start == null, start, end: e, reason_id: i.reason_id, comment: i.comment ?? null, cancel_reason: null });
  }

  cancelInterruption(i: RentalInterruption): void {
    this.mode = 'cancel';
    this.targetId = i.id;
    this.errorMsg = '';
    this.savedNotice = null;
    this.form.patchValue({ cancel_reason: null });
  }

  private payload(): RentalDecisionPayload {
    const v = this.form.getRawValue();
    const base = { order_id: this.data.orderId, line_id: this.data.lineId };
    const cancel_reason = (v.cancel_reason ?? '').trim();
    if (this.mode === 'cancel') return { ...base, op: 'cancel', id: this.targetId!, cancel_reason };
    // Jours saisis => midi de Paris (début, et dernier jour inclus) ; une valeur non convertible part telle quelle (le serveur répond 400).
    const start = v.fromDelivery || !v.start ? null : (dayNoonIso(v.start) ?? v.start);
    const end = v.end ? (dayNoonIso(v.end) ?? v.end) : null;
    const entry = { start, end, reason_id: v.reason_id!, comment: (v.comment ?? '').trim() || null };
    return this.mode === 'add' ? { ...base, op: 'add', ...entry } : { ...base, op: 'replace', id: this.targetId!, cancel_reason, ...entry };
  }

  submit(): void {
    this.form.updateValueAndValidity();
    if (!this.canWrite || this.busy || this.form.invalid) return;
    this.busy = true;
    this.errorMsg = '';
    this.savedNotice = null;
    const payload = this.payload();
    const notice = payload.op === 'cancel' ? null : this.fseNotice(payload.reason_id);
    this.quotationsService.postRentalDecisionEvent(EventType.RENTAL_INTERRUPTION, payload, this.loginService.loggeduser?.id).subscribe({
      next: res => {
        this.busy = false;
        if (!res?.success) {
          this.errorMsg = res?.msg || res?.error_msg || 'Décision refusée par le serveur';
        } else {
          this.interruptions = this.sorted(res.data?.rental_lifecycle?.interruptions ?? []);
          this.changed = true;
          this.data.onDecision?.();
          this.resetForm();
          // Rappel gardé après l'enregistrement : c'est maintenant que la caisse doit être avisée.
          if (notice) this.savedNotice = { id: res.data?.decision_id ?? null, text: notice };
        }
        this.cdr.markForCheck();
      },
      error: e => {
        this.busy = false;
        this.errorMsg = `Erreur serveur : ${e?.error?.message ?? e?.error ?? e?.message ?? e}`;
        this.cdr.markForCheck();
      },
    });
  }

  close(): void {
    this.dialogRef.close(this.changed);
  }
}
